import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import { grey } from "@mui/material/colors";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AuthState } from "context/AuthContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { getLookerConversationUrl, LOOKER_AGENT_PAGE_KEYS } from "../lookerAgentConfig";

const signedEmbedEndpoint = "/api/reports/looker-agent/signed-embed-url";

const getEmbedPath = (url) => {
	try {
		const parsedUrl = new URL(url);
		return parsedUrl.pathname + parsedUrl.search;
	} catch {
		return "";
	}
};


const getSignedUrlFromResponse = (data) => data?.signed_embed_url || data?.embed_url || data?.url || "";

const LookerAgent = () => {
	const { user } = AuthState();
	const lookerTargetUrl = useMemo(
		() =>
			getLookerConversationUrl({
				pageKey: LOOKER_AGENT_PAGE_KEYS.LOOKER_AGENT,
				embed: true,
			}),
		[]
	);
	const [signedEmbedUrl, setSignedEmbedUrl] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [sessionExpired, setSessionExpired] = useState(false);

	const loadSignedEmbedUrl = useCallback(async () => {
		if (!lookerTargetUrl) {
			setError("Looker Agent target URL is not configured.");
			return;
		}

		setLoading(true);
		setError("");
		setSessionExpired(false);

		try {
			const { data } = await pythonVmInstance.post(signedEmbedEndpoint, {
				target_url: lookerTargetUrl,
				embed_path: getEmbedPath(lookerTargetUrl),
				user: {
					email: user?.email || "",
					name: user?.name || user?.email || "Rarebox User",
				},
			});

			const nextSignedUrl = getSignedUrlFromResponse(data);
			if (!nextSignedUrl) throw new Error("Signed Looker URL missing from backend response.");
			setSignedEmbedUrl(nextSignedUrl);
			setSessionExpired(false);
		} catch (requestError) {
			setSignedEmbedUrl("");
			setError(requestError?.response?.data?.detail || requestError?.response?.data?.message || requestError.message || "Unable to load Looker Agent.");
		} finally {
			setLoading(false);
		}
	}, [lookerTargetUrl, user?.email, user?.name]);

	useEffect(() => {
		loadSignedEmbedUrl();
	}, [loadSignedEmbedUrl]);

	if (loading && !signedEmbedUrl) {
		return (
			<Stack
				width="100%"
				height= "calc(100vh - 64px - 56px)"
				alignItems="center"
				justifyContent="center"
				sx={{ backgroundColor: grey[200], p: 2, boxSizing: "border-box" }}>
				<CircularProgress size={28} />
				<Typography fontSize={14} color="text.secondary" sx={{ mt: 1.5 }}>
					Loading Looker Agent
				</Typography>
			</Stack>
		);
	}

	if (error && !signedEmbedUrl) {
		return (
			<Stack
				width="100%"
				height= "calc(100vh - 64px - 56px)"
				alignItems="center"
				justifyContent="center"
				sx={{ backgroundColor: grey[200], p: 2, boxSizing: "border-box" }}>
				<Typography variant="h6" fontSize={16}>
					Unable to load Looker Agent
				</Typography>
				<Typography fontSize={14} color="text.secondary" sx={{ mt: 0.5, maxWidth: 520, textAlign: "center" }}>
					{error}
				</Typography>
				<Button variant="contained" startIcon={<RefreshRounded />} onClick={loadSignedEmbedUrl} sx={{ mt: 2 }}>
					Refresh session
				</Button>
			</Stack>
		);
	}

	return (
		<Stack
			width="100%"
			
			alignItems="stretch"
			sx={{
				backgroundColor: grey[200],
				p: 2,
				boxSizing: "border-box",
				gap: 1,
				height: "calc(100vh - 64px - 56px)",
			}}>

			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					width: "100%",
					borderRadius: 1,
					overflow: "hidden",
					boxShadow: 2,
					backgroundColor: "common.white",
				position: "relative",
			}}>
				<iframe
					title="Looker Agent"
					width="100%"
					height="100%"
					src={signedEmbedUrl}
					frameBorder="0"
					style={{
						border: 0,
						display: "block",
						minHeight: "100%",
					}}
					allow="clipboard-read; clipboard-write; fullscreen"
					sandbox="allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
					allowFullScreen
					onError={() => setSessionExpired(true)}
				/>
				{sessionExpired && (
					<Stack
						alignItems="center"
						justifyContent="center"
						sx={{
							position: "absolute",
							inset: 0,
							backgroundColor: "rgba(255, 255, 255, 0.92)",
							zIndex: 1,
						}}>
						<Typography variant="h6" fontSize={16}>
							Looker session expired
						</Typography>
						<Typography fontSize={14} color="text.secondary" sx={{ mt: 0.5, mb: 2, textAlign: "center" }}>
							Refresh the session to continue using Looker Agent.
						</Typography>
						<Button variant="contained" startIcon={<RefreshRounded />} onClick={loadSignedEmbedUrl} disabled={loading}>
							Refresh session
						</Button>
					</Stack>
				)}
			</Box>
		</Stack>
	);
};

export default LookerAgent;








